import React, { useRef, useState } from 'react'
import { COLORS, RADIUS, FONT } from '../../styles/tokens'
import type { ValidMinute } from './HoursData'

// ── Shared clock geometry ─────────────────────────────────────────

function clockAngles(hour12: number, minute: number) {
  const minuteAngle = (minute / 60) * 360
  const hourAngle   = ((hour12 % 12) / 12) * 360 + (minute / 60) * 30
  return { minuteAngle, hourAngle }
}

function polarToXY(cx: number, cy: number, angleDeg: number, r: number) {
  const rad = (angleDeg - 90) * (Math.PI / 180)
  return { x: cx + Math.cos(rad) * r, y: cy + Math.sin(rad) * r }
}

function to12h(h: number): number { return h % 12 || 12 }

// ── Static Analog Clock ───────────────────────────────────────────

interface AnalogClockProps {
  hour:        number
  minute:      number
  size?:       number
  highlighted?: boolean
  shake?:       boolean
}

export function AnalogClock({ hour, minute, size = 160, highlighted, shake }: AnalogClockProps) {
  const cx = size / 2
  const cy = size / 2
  const faceR  = cx - 4
  const h12    = to12h(hour)
  const { minuteAngle, hourAngle } = clockAngles(h12, minute)

  const minEnd = polarToXY(cx, cy, minuteAngle, faceR * 0.72)
  const hrEnd  = polarToXY(cx, cy, hourAngle,   faceR * 0.50)

  const numbers = Array.from({ length: 12 }, (_, i) => {
    const n   = i + 1
    const pos = polarToXY(cx, cy, (n / 12) * 360, faceR * 0.76)
    return { n, ...pos }
  })

  return (
    <svg
      width={size}
      height={size}
      className={shake ? 'wm-shake' : undefined}
      style={{
        display:      'block',
        borderRadius: '50%',
        border:       highlighted
          ? `3px solid ${COLORS.greenLight}`
          : `2px solid ${COLORS.borderWarm}`,
        transition:   'border-color 0.2s',
        background:   COLORS.bgCard,
        flexShrink:   0,
      }}
    >
      {/* Face */}
      <circle cx={cx} cy={cy} r={faceR} fill={COLORS.bgCard} />

      {/* Tick marks */}
      {Array.from({ length: 12 }, (_, i) => {
        const a   = (i / 12) * 360
        const p1  = polarToXY(cx, cy, a, faceR)
        const p2  = polarToXY(cx, cy, a, faceR - 8)
        return (
          <line
            key={i}
            x1={p1.x} y1={p1.y}
            x2={p2.x} y2={p2.y}
            stroke={COLORS.borderWarm}
            strokeWidth={2}
          />
        )
      })}

      {/* Hour numbers */}
      {numbers.map(({ n, x, y }) => (
        <text
          key={n}
          x={x} y={y}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={Math.round(size * 0.10)}
          fontWeight="700"
          fill={COLORS.textPrimary}
          fontFamily="Nunito, sans-serif"
        >
          {n}
        </text>
      ))}

      {/* Hour hand — short, thick, dark green */}
      <line
        x1={cx} y1={cy}
        x2={hrEnd.x} y2={hrEnd.y}
        stroke={COLORS.greenDark}
        strokeWidth={Math.round(size * 0.048)}
        strokeLinecap="round"
      />

      {/* Minute hand — longer, thinner, mid green */}
      <line
        x1={cx} y1={cy}
        x2={minEnd.x} y2={minEnd.y}
        stroke={COLORS.green}
        strokeWidth={Math.round(size * 0.028)}
        strokeLinecap="round"
      />

      {/* Center dot */}
      <circle cx={cx} cy={cy} r={Math.round(size * 0.05)} fill={COLORS.greenDark} />
    </svg>
  )
}

// ── Static Digital Clock ──────────────────────────────────────────

interface DigitalClockDisplayProps {
  hour:         number
  minute:       number
  highlighted?: boolean
  shake?:       boolean
}

export function DigitalClockDisplay({ hour, minute, highlighted, shake }: DigitalClockDisplayProps) {
  const hStr = String(hour).padStart(2, '0')
  const mStr = String(minute).padStart(2, '0')
  return (
    <div
      className={shake ? 'wm-shake' : undefined}
      style={{
        fontFamily:   'Nunito, monospace',
        fontSize:     FONT.size.h2,
        fontWeight:   800,
        letterSpacing: '0.08em',
        color:        COLORS.textPrimary,
        background:   COLORS.bgCard,
        border:       highlighted
          ? `3px solid ${COLORS.greenLight}`
          : `2px solid ${COLORS.borderWarm}`,
        borderRadius: RADIUS.card,
        padding:      '14px 28px',
        minWidth:     120,
        textAlign:    'center',
        transition:   'border-color 0.2s',
      }}
    >
      {hStr}<span style={{ opacity: 0.5 }}>:</span>{mStr}
    </div>
  )
}

// ── Interactive Analog Clock ──────────────────────────────────────

interface InteractiveAnalogClockProps {
  hour:    number       // 1–12
  minute:  ValidMinute
  size?:   number
  onHourAngle:   (deg: number) => void
  onMinuteAngle: (deg: number) => void
  shake?:  boolean
}

export function InteractiveAnalogClock({
  hour, minute, size = 200, onHourAngle, onMinuteAngle, shake,
}: InteractiveAnalogClockProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const [dragging, setDragging] = useState<'hour' | 'minute' | null>(null)

  const cx = size / 2
  const cy = size / 2
  const faceR = cx - 6

  const h12 = to12h(hour)
  const minuteAngle = (minute / 60) * 360
  // Hour hand drifts with minute: at :30 the hand sits exactly between two hour marks
  const hourAngle   = ((h12 % 12) / 12) * 360 + (minute / 60) * 30

  const minEnd   = polarToXY(cx, cy, minuteAngle, faceR * 0.72)
  const hrEnd    = polarToXY(cx, cy, hourAngle,   faceR * 0.50)
  const minGrab  = polarToXY(cx, cy, minuteAngle, faceR * 0.72)
  const hrGrab   = polarToXY(cx, cy, hourAngle,   faceR * 0.50)

  const numbers = Array.from({ length: 12 }, (_, i) => {
    const n   = i + 1
    const pos = polarToXY(cx, cy, (n / 12) * 360, faceR * 0.76)
    return { n, ...pos }
  })

  function getAngleDeg(clientX: number, clientY: number): number {
    const svg  = svgRef.current
    if (!svg) return 0
    const rect = svg.getBoundingClientRect()
    const dx   = clientX - (rect.left + rect.width  / 2)
    const dy   = clientY - (rect.top  + rect.height / 2)
    return Math.atan2(dy, dx) * (180 / Math.PI) + 90  // 0° = top (12 o'clock)
  }

  const handleGrabDown = (hand: 'hour' | 'minute') => (e: React.PointerEvent<SVGCircleElement>) => {
    e.preventDefault()
    svgRef.current?.setPointerCapture(e.pointerId)
    setDragging(hand)
  }

  const handleSVGMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!dragging) return
    const deg = getAngleDeg(e.clientX, e.clientY)
    if (dragging === 'minute') onMinuteAngle(deg)
    else                       onHourAngle(deg)
  }

  const handleSVGUp = () => setDragging(null)

  const grabR = Math.round(size * 0.07)  // large grab targets for elderly users

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      className={shake ? 'wm-shake' : undefined}
      onPointerMove={handleSVGMove}
      onPointerUp={handleSVGUp}
      onPointerLeave={handleSVGUp}
      style={{ display: 'block', touchAction: 'none', cursor: dragging ? 'grabbing' : 'default', flexShrink: 0 }}
    >
      {/* Face */}
      <circle cx={cx} cy={cy} r={faceR} fill={COLORS.bgCard} stroke={COLORS.borderWarm} strokeWidth={2} />

      {/* Tick marks */}
      {Array.from({ length: 12 }, (_, i) => {
        const a  = (i / 12) * 360
        const p1 = polarToXY(cx, cy, a, faceR)
        const p2 = polarToXY(cx, cy, a, faceR - 10)
        return <line key={i} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={COLORS.borderWarm} strokeWidth={2} />
      })}

      {/* Numbers */}
      {numbers.map(({ n, x, y }) => (
        <text
          key={n}
          x={x} y={y}
          textAnchor="middle" dominantBaseline="central"
          fontSize={Math.round(size * 0.09)}
          fontWeight="700"
          fill={COLORS.textPrimary}
          fontFamily="Nunito, sans-serif"
        >
          {n}
        </text>
      ))}

      {/* Hour hand */}
      <line
        x1={cx} y1={cy} x2={hrEnd.x} y2={hrEnd.y}
        stroke={COLORS.greenDark}
        strokeWidth={Math.round(size * 0.048)}
        strokeLinecap="round"
      />

      {/* Minute hand */}
      <line
        x1={cx} y1={cy} x2={minEnd.x} y2={minEnd.y}
        stroke={COLORS.green}
        strokeWidth={Math.round(size * 0.028)}
        strokeLinecap="round"
      />

      {/* Center dot */}
      <circle cx={cx} cy={cy} r={Math.round(size * 0.045)} fill={COLORS.greenDark} />

      {/* Hour grab handle */}
      <circle
        cx={hrGrab.x} cy={hrGrab.y} r={grabR}
        fill={COLORS.greenDark}
        opacity={0.9}
        onPointerDown={handleGrabDown('hour')}
        style={{ cursor: 'grab', touchAction: 'none' }}
        aria-label="Ajustar hora"
      />

      {/* Minute grab handle */}
      <circle
        cx={minGrab.x} cy={minGrab.y} r={grabR}
        fill={COLORS.green}
        opacity={0.9}
        onPointerDown={handleGrabDown('minute')}
        style={{ cursor: 'grab', touchAction: 'none' }}
        aria-label="Ajustar minutos"
      />

      {/* Labels inside grab handles */}
      <text
        x={hrGrab.x} y={hrGrab.y}
        textAnchor="middle" dominantBaseline="central"
        fontSize={Math.round(size * 0.065)}
        fontWeight="800"
        fill="white"
        fontFamily="Nunito, sans-serif"
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        H
      </text>
      <text
        x={minGrab.x} y={minGrab.y}
        textAnchor="middle" dominantBaseline="central"
        fontSize={Math.round(size * 0.065)}
        fontWeight="800"
        fill="white"
        fontFamily="Nunito, sans-serif"
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        M
      </text>
    </svg>
  )
}

// ── Digital Spinner ───────────────────────────────────────────────

interface DigitalSpinnerProps {
  hour:       number
  minute:     ValidMinute
  is24h?:     boolean
  onStepHour:   (delta: number) => void
  onStepMinute: (delta: number) => void
  shake?:     boolean
}

export function DigitalSpinner({
  hour, minute, is24h = true, onStepHour, onStepMinute, shake,
}: DigitalSpinnerProps) {
  const hStr = String(hour).padStart(2, '0')
  const mStr = String(minute).padStart(2, '0')

  const btnStyle: React.CSSProperties = {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    width:          52,
    height:         52,
    fontSize:       FONT.size.xl,
    fontWeight:     700,
    color:          COLORS.greenDark,
    background:     COLORS.bgCard,
    border:         `2px solid ${COLORS.borderWarm}`,
    borderRadius:   RADIUS.md,
    cursor:         'pointer',
    touchAction:    'manipulation',
  }

  const digitStyle: React.CSSProperties = {
    fontFamily:    'Nunito, monospace',
    fontSize:      FONT.size.h2,
    fontWeight:    800,
    color:         COLORS.textPrimary,
    letterSpacing: '0.04em',
    minWidth:      64,
    textAlign:     'center',
    lineHeight:    1,
  }

  const colStyle: React.CSSProperties = {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    gap:            10,
  }

  return (
    <div
      className={shake ? 'wm-shake' : undefined}
      style={{
        display:        'flex',
        alignItems:     'center',
        gap:            12,
        background:     COLORS.bgCard,
        border:         `2px solid ${COLORS.borderWarm}`,
        borderRadius:   RADIUS.card,
        padding:        '20px 24px',
      }}
    >
      <div style={colStyle}>
        <button style={btnStyle} onClick={() => onStepHour(+1)} aria-label="Subir hora">▲</button>
        <span style={digitStyle}>{hStr}</span>
        <button style={btnStyle} onClick={() => onStepHour(-1)} aria-label="Bajar hora">▼</button>
      </div>

      <span style={{ fontSize: FONT.size.h2, fontWeight: 800, color: COLORS.textPrimary, opacity: 0.4 }}>:</span>

      <div style={colStyle}>
        <button style={btnStyle} onClick={() => onStepMinute(+1)} aria-label="Subir minutos">▲</button>
        <span style={digitStyle}>{mStr}</span>
        <button style={btnStyle} onClick={() => onStepMinute(-1)} aria-label="Bajar minutos">▼</button>
      </div>

      {!is24h && (
        <span style={{ fontSize: FONT.size.sm, color: COLORS.textSecondary, alignSelf: 'flex-end', paddingBottom: 14 }}>
          12h
        </span>
      )}
    </div>
  )
}

// ── Small clock for MCQ options ───────────────────────────────────

export function SmallAnalogClock({ hour, minute, size = 90 }: { hour: number; minute: number; size?: number }) {
  const cx    = size / 2
  const cy    = size / 2
  const faceR = cx - 3
  const h12   = to12h(hour)
  const { minuteAngle, hourAngle } = clockAngles(h12, minute)
  const minEnd = polarToXY(cx, cy, minuteAngle, faceR * 0.70)
  const hrEnd  = polarToXY(cx, cy, hourAngle,   faceR * 0.48)

  const numbers = Array.from({ length: 12 }, (_, i) => {
    const n = i + 1
    return { n, ...polarToXY(cx, cy, (n / 12) * 360, faceR * 0.72) }
  })

  return (
    <svg width={size} height={size} style={{ display: 'block', flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={faceR} fill={COLORS.bgCard} stroke={COLORS.borderWarm} strokeWidth={1.5} />
      {numbers.map(({ n, x, y }) => (
        <text
          key={n} x={x} y={y}
          textAnchor="middle" dominantBaseline="central"
          fontSize={Math.round(size * 0.09)}
          fontWeight="700"
          fill={COLORS.textPrimary}
          fontFamily="Nunito, sans-serif"
        >
          {n}
        </text>
      ))}
      <line x1={cx} y1={cy} x2={hrEnd.x}  y2={hrEnd.y}  stroke={COLORS.greenDark} strokeWidth={Math.round(size * 0.05)} strokeLinecap="round" />
      <line x1={cx} y1={cy} x2={minEnd.x} y2={minEnd.y} stroke={COLORS.green}     strokeWidth={Math.round(size * 0.03)} strokeLinecap="round" />
      <circle cx={cx} cy={cy} r={Math.round(size * 0.06)} fill={COLORS.greenDark} />
    </svg>
  )
}

export function SmallDigitalClock({ hour, minute }: { hour: number; minute: number }) {
  const hStr = String(hour).padStart(2, '0')
  const mStr = String(minute).padStart(2, '0')
  return (
    <div style={{
      fontFamily:    'Nunito, monospace',
      fontSize:      FONT.size.lg,
      fontWeight:    800,
      letterSpacing: '0.05em',
      color:         COLORS.textPrimary,
    }}>
      {hStr}<span style={{ opacity: 0.45 }}>:</span>{mStr}
    </div>
  )
}

